import React from "react"
import _ from "lodash"
import StudentQualityList from "../../components/StudentQualityList"
import "../../assets/css/pages/track_progress/frequency-chart-submission.css"
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Text,
  Label,
  XAxis,
  YAxis,
  ScatterChart,
  Scatter,
  CartesianGrid,
  Dot,
  Tooltip,
  Rectangle
} from "recharts"
import SolutionRow from "../../components/SolutionRow"

const DOTPLOT = "dot-plot"
const HISTOGRAM = "histogram"
const TALLY = "tally"
const HISTOTALLY = "histo-tally"

interface SubmissionDataType {
  taskMode: TaskModeType
  taskUuid: string
  description: string
  chartDescription: ChartDescriptionType
  correctAnswer: correctAnswerType
  wrongAnswers: { answer: {}; percentage: number }[]
  gradedStudents: {}
}

type TaskModeType = "dot-plot" | "histogram" | "tally" | "histo-tally"

interface ChartDescriptionType {
  chartTitle: string
  xItemNameHeader: string
  xValueHeader: string
  xAxis?: AxisType
  yAxis?: AxisType
}

interface correctAnswerType {
  num_placed_on_chart: number
  placed_table_keys: any
  chart_points: any
}
interface AxisType {
  title?: string
  unitName?: string
  min: number
  max: number
  interval: number
}

const FrequencyChartSubmissions = (submissionData: SubmissionDataType) => {
  const { taskMode, gradedStudents, chartDescription, description, correctAnswer, wrongAnswers } =
    submissionData
  const [viewAll, setViewAll] = React.useState<number>(2)
  const generateChart = (type: TaskModeType, chartDescription: ChartDescriptionType, data) => {
    switch (type) {
      case DOTPLOT:
        return <DotChart data={generateDotChartData(chartDescription, data)} />
      case HISTOGRAM:
      case HISTOTALLY:
        return <Histogram data={generateHistogramData(chartDescription, data)} />
      case TALLY:
        return <FrequencyChart data={generateTableData(chartDescription, data, TALLY)} />
      default:
        return
    }
  }

  const generateTableData = (
    chartDescription: ChartDescriptionType,
    data: {
      correctAnswer?: correctAnswerType
      wrongAnswer?: any
    },
    type?: string
  ): DataTableProp => {
    const { xItemNameHeader, xValueHeader } = chartDescription
    const { correctAnswer, wrongAnswer } = data
    return {
      xValueHeader: type === HISTOTALLY ? "Tally" : xItemNameHeader,
      yValueHeader: type === HISTOTALLY ? "Frequency" : xValueHeader,
      items: Object.entries(
        correctAnswer?.[
          _.includes([TALLY, HISTOTALLY], type) ? "chart_points" : "placed_table_keys"
        ] || wrongAnswer.answer
      ).map(([name, value]: [string, string | number]) => {
        return {
          name: name,
          value: value
        }
      })
    }
  }

  const generateChartAxis = (title: string, xAxis: AxisType, yAxis: AxisType) => {
    return {
      title,
      xAxis: {
        title: xAxis.title,
        ...(_.isNumber(xAxis?.min) && { min: xAxis.min }),
        ...(xAxis?.max && { max: xAxis.max }),
        ...(xAxis?.interval && { interval: xAxis.interval })
      },
      yAxis: {
        title: yAxis.title,
        min: yAxis.min,
        max: yAxis.max,
        interval: yAxis.interval
      }
    }
  }

  const generateHistogramData = (chartDescription: ChartDescriptionType, data): HistogramProp => {
    const { chartTitle, xAxis, yAxis } = chartDescription
    const { correctAnswer, wrongAnswer } = data
    return {
      ...generateChartAxis(chartTitle, xAxis, yAxis),
      items: Object.entries(correctAnswer?.chart_points || wrongAnswer.answer).map(
        ([answerName, answerVal]: [string, number]) => ({
          name: answerName,
          value: answerVal
        })
      )
    }
  }

  const generateDotChartData = (chartDescription: ChartDescriptionType, data): DotChartProp => {
    const { chartTitle, xAxis, yAxis } = chartDescription
    const { correctAnswer, wrongAnswer } = data
    return {
      ...generateChartAxis(chartTitle, xAxis, yAxis),
      items: Object.entries(correctAnswer?.chart_points || wrongAnswer.answer).reduce(
        (items, [x, y]: [string, number]) => {
          for (let i = 1; i <= y; i++) {
            items.push({
              x: parseInt(x),
              y: i
            })
          }
          return items
        },
        []
      )
    }
  }

  return (
    <div className={`frequency-chart-submissions ${taskMode}`}>
      <p className='taskDescription'>
        <span>Task:</span>
        {description}
      </p>
      {!_.isEmpty(correctAnswer) && (
        <SolutionRow type='correct'>
          <div className={`correctAnswer ${taskMode}`}>
            <DataTable
              data={generateTableData(
                chartDescription,
                { correctAnswer },
                taskMode === HISTOTALLY && HISTOTALLY
              )}
            />
            {generateChart(taskMode, chartDescription, { correctAnswer })}
          </div>
        </SolutionRow>
      )}
      <div className={`incorrectAnswers ${taskMode}`}>
        {wrongAnswers.slice(0, viewAll).map(wrongAnswer => (
          <SolutionRow type='incorrect' percent={wrongAnswer.percentage}>
            <div className={`incorrectAnswer ${taskMode}`}>
              {generateChart(taskMode, chartDescription, { wrongAnswer })}
            </div>
          </SolutionRow>
        ))}
      </div>
      <StudentQualityList students={gradedStudents} title={"Status Breakdown"} />
      {wrongAnswers.length > 2 && (
        <div className='viewAll'>
          {viewAll > 2 && (
            <button
              className='viewAllButton'
              onClick={() => {
                setViewAll(2)
              }}
            >
              &lt; View Less
            </button>
          )}
          {viewAll < wrongAnswers.length && (
            <button
              className='viewAllButton'
              onClick={() => {
                setViewAll(prev => (prev < wrongAnswers.length ? (prev += 4) : prev))
              }}
            >
              View More &gt;
            </button>
          )}
        </div>
      )}
    </div>
  )
}

interface DataTableProp {
  xValueHeader?: string
  yValueHeader?: string
  items: { name: string; value: number | string }[]
  taskUuid?: string
}

const DataTable = ({ data }: { data: DataTableProp }) => {
  const { xValueHeader, yValueHeader, items, taskUuid } = data
  return (
    <table className='DataTable'>
      <thead className='DataTable__head'>
        <tr>
          <th>{xValueHeader}</th>
          <th>{yValueHeader}</th>
        </tr>
      </thead>
      <tbody className='DataTable__body'>
        {items.map((item, itemInd) => (
          <tr key={`prompt-${taskUuid}-${itemInd}`}>
            <td>{item.name}</td>
            <td>{item.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const TallyBundle = () => {
  return (
    <div className='tally-bundle'>
      ||||
      <span className='tally-bundle--cross'>|</span>
    </div>
  )
}

const FrequencyChart = ({ data }: { data: DataTableProp }) => {
  const { items, taskUuid } = data
  const generateTally = (count: number) => {
    const bundleCount = Math.floor(count / 5)
    const remainder = count % 5
    let tallys = []
    for (let count = 0; count < bundleCount; count++) {
      tallys.push(<TallyBundle />)
    }
    for (let count = 0; count < remainder; count++) {
      tallys.push("|")
    }
    return tallys.map(tally => tally)
  }
  return (
    <table className='FrequencyChart'>
      <thead className='FrequencyChart__head'>
        <tr>
          <th>Header</th>
          <th>Tally</th>
          <th>Frequency</th>
        </tr>
      </thead>
      <tbody className='FrequencyChart__body'>
        {items.map((item, itemInd) => (
          <tr key={`frequency-${taskUuid}-${itemInd}`}>
            <td>{item.name}</td>
            <td>{generateTally(+item.value)}</td>
            <td>{item.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

interface ChartDataProp {
  title: string
  xAxis?: AxisType
  yAxis?: AxisType
}

interface HistogramProp extends ChartDataProp {
  items: { name: string; value: number }[]
}

const Histogram = ({ data }: { data: HistogramProp }) => {
  const { title, xAxis, yAxis, items } = data

  const generateYAxis = yAxis => {
    let axisTicks = []
    for (let i = yAxis.min; i <= yAxis.max; i += yAxis.interval) {
      axisTicks.push(i)
    }
    return axisTicks
  }
  return (
    <div className='Histogram'>
      <p className='chartTitle'>{title}</p>
      <ResponsiveContainer width='100%' height={450}>
        <BarChart
          data={items}
          barCategoryGap='1'
          margin={{ top: 12, right: 0, bottom: 20, left: 0 }}
        >
          <Text scaleToFit={true}>{title}</Text>
          <Bar dataKey='value' fill='#2AD9F4' activeBar={<Rectangle fill='#48b0f7' />} />
          <XAxis dataKey='name' stroke='#FFFFFF' tickLine={false} strokeWidth={2}>
            <Label
              className='labelText'
              value={xAxis?.title}
              offset={-12}
              position='insideBottom'
            />
          </XAxis>
          <YAxis
            type='number'
            dataKey='value'
            name='value'
            stroke='white'
            domain={[yAxis.min, yAxis.max + 2]}
            ticks={generateYAxis(yAxis)}
            strokeWidth={2}
          />
          <Tooltip cursor={{ fill: "white", opacity: "0.15" }} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

interface DotChartProp extends ChartDataProp {
  items: {
    x?: string | number
    y?: string | number
    z?: string | number
  }[]
}
const DotChart = ({ data }: { data: DotChartProp }) => {
  const { title, xAxis, yAxis, items } = data
  const generateXAxis = xAxis => {
    let arrayTicks = []
    for (let i = xAxis.min; i <= xAxis.max; i += xAxis.interval) {
      arrayTicks.push(i)
    }
    return arrayTicks
  }

  return (
    <div className='DotChart'>
      <p className='chartTitle'>{title}</p>
      <ResponsiveContainer width='100%' height={450}>
        <ScatterChart
          margin={{
            top: 12,
            right: 20,
            bottom: 20,
            left: 20
          }}
        >
          <CartesianGrid horizontal={false} vertical={true} className='guidingLine' />
          <XAxis
            className='axis'
            type='number'
            dataKey='x'
            name='x'
            stroke='white'
            domain={[xAxis.min - 1, xAxis.max + 1]}
            ticks={generateXAxis(xAxis)}
            padding='gap'
            strokeWidth={2}
          >
            <Label
              value={xAxis?.title}
              offset={-12}
              position='insideBottom'
              className='labelText'
            />
          </XAxis>
          <YAxis
            type='number'
            dataKey='y'
            domain={[0, yAxis?.max ? yAxis.max + 1 : "dataMax + 1"]}
            className='axis'
            stroke='white'
            hide={true}
          />
          <Scatter name={title} data={items} fill='#2AD9F4' shape={<Dot r={12} />} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  )
}

export default FrequencyChartSubmissions
